import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../components/header/header.component';
import { GlobalService } from '../../services/global.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { AgGridListComponent } from "../../components/ag-grid-list/ag-grid-list.component";
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { ButtonComponent } from '../../components/button/button.component';
import { PaymentReceiptComponent } from '../../components/payment-receipt/payment-receipt.component';
import { ProjectSettingsService } from '../../services/project-settings.service';
import { PaymentService } from '../../services/payment.service';

@Component({
  selector: 'app-transaction-history-list-page',
  imports: [HeaderComponent, CommonModule, ModalComponent, AgGridListComponent, SearchInputComponent, ButtonComponent, PaymentReceiptComponent],
  templateUrl: './transaction-history-list-page.component.html',
})
export class TransactionHistoryListPageComponent {
  @ViewChild('receipt_modal') receipt_modal: PaymentReceiptComponent | undefined;

  page_name = 'Payment History';
  params: any = {};
  selected_ids: any = {}
  items: any

  transaction_history_cols: any = [
    { field: 'academic_year', headerName: 'Academic Year', filter: true, flex: 1 },
    {
      headerName: 'Program', filter: true, flex: 1,
      valueGetter: (p: any) => {
        const prog = p.data?.program;
        const code = p.data?.program_code;
        if (!prog) return '—';
        return code ? `${prog} (${code})` : prog;
      }
    },
    { field: 'exam', headerName: 'Exam', filter: true, flex: 1 },
    { field: 'subject', headerName: 'Subject', filter: true, flex: 1 },
    {
      field: 'payment_date', headerName: 'Payment Date', filter: true, flex: 1,
      valueFormatter: (p: any) => {
        const raw = p.data?.payment_date || p.data?.createdAt;
        if (!raw) return '—';
        const d = new Date(raw);
        if (isNaN(d.getTime())) return String(raw);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    },
    {
      headerName: 'Payment Status', filter: true, flex: 1,
      cellRenderer: (p: any) => {
        const raw = String(p.data?.status || '');
        const s = raw.toLowerCase();
        const is_success = s === 'success';
        const is_failed = s === 'failed' || s === 'cancelled' || s === 'expired';
        const label = is_success ? 'Success' : is_failed ? 'Failed' : (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');
        const color = is_success ? '#16a34a' : is_failed ? '#9F0712' : '#d97706';
        const bg = is_success ? '#dcfce7' : is_failed ? '#FFE2E2' : '#fef9c3';
        return `<span style="background:${bg};color:${color};font-weight:600;font-size:14px;width:104px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;height:29.62px;box-sizing:border-box">${label}</span>`;
      }
    },
    {
      headerName: '', sortable: false, filter: false,
      headerClass: 'custom-header-center',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: (p: any) => {
        const is_success = (p.data?.status || '').toLowerCase() === 'success';
        const color = is_success ? '#3F41D1' : '#9ca3af';
        const bg = is_success ? '#eaebff' : '#f3f4f6';
        const cursor = is_success ? 'pointer' : 'default';
        return `<button style="background:${bg};border:none;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:${cursor}">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </button>`;
      },
      onCellClicked: (p: any) => { if ((p.data?.status || '').toLowerCase() === 'success') this.open_receipt(p.data); },
      editable: false, pinned: 'right', width: 100, minWidth: 100, maxWidth: 100
    },
  ];

  constructor(
    public gs: GlobalService,
    public route: Router,
    public ar: ActivatedRoute,
    public ps: ProjectSettingsService,
    public payment_service: PaymentService,
  ) { }

  async ngOnInit() {
    this.ar.queryParams.subscribe(async (params: any) => {
      this.params = { ...params }
      this.get_list(this.params);
    })
  }

  download() {
    const data: any[] = this.items?.data || [];
    if (!data.length) return;
    const escape = (v: any) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; };
    const format_date = (raw: any) => {
      if (!raw) return '';
      const d = new Date(raw);
      if (isNaN(d.getTime())) return String(raw);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    const headers = ['Academic Year', 'Program', 'Exam', 'Subject', 'Payment Date', 'Payment Status'];
    let csv = headers.join(',') + '\n';
    for (const row of data) {
      const prog = row.program_code ? `${row.program} (${row.program_code})` : (row.program || '');
      const date_str = format_date(row.payment_date || row.createdAt);
      const status_str = row.status ? String(row.status).charAt(0).toUpperCase() + String(row.status).slice(1).toLowerCase() : '';
      csv += [escape(row.academic_year), escape(prog), escape(row.exam), escape(row.subject), escape(date_str), escape(status_str)].join(',') + '\n';
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Payment_History.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  async open_receipt(item: any) {
    try {
      const config_res = await this.ps.get_config(item.university_id || this.gs.user_data?.university_id);
      if (!config_res?.data?.admin_config_required) {
        this.gs.toastr_shows_function('This feature has been disabled by the administrator.', 'Error', 'error');
        return;
      }
      const res = await this.ps.download_receipt(item.id);
      if (res?.data && this.receipt_modal) {
        this.receipt_modal.receipt_data = res.data;
        this.receipt_modal.print_receipt();
      } else {
        this.gs.toastr_shows_function('Receipt data not available.', 'Error', 'error');
      }
    } catch {
      this.gs.toastr_shows_function('Failed to load receipt.', 'Error', 'error');
    }
  }

  async get_list(filter?: any) {
    try {
      const merged = { ...(this.params || {}), ...(filter || {}) };
      const res = await this.payment_service.get_list(merged);
      const page = Number(merged?.page) > 0 ? Number(merged.page) : 1;
      const page_size = Number(merged?.page_size) > 0 ? Number(merged.page_size) : 10;
      const start = (page - 1) * page_size;
      const data = res?.data || [];
      this.items = { ...res, data: data.map((item: any, i: number) => ({ ...item, SrNo: start + i + 1 })) };
    } catch (error: any) {
      console.error("Error fetching payment list:", error);
      this.gs.toastr_shows_function(error?.error?.message || 'Failed to load payment history.', 'Error', 'error');
    }
  }
}
